import React, {
  createContext,
  useContext,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState
} from 'react'
import { tv } from 'tailwind-variants'
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { ChevronDown, Cross, Drag, Plus, Tick } from 'components/icons'
import Button from 'components/button'
import Menu from 'components/menu'
import Input from 'components/input'
import Toggle from 'components/toggle'
import { MenuItem, MenuLabel, MenuList } from 'components/menu/items'
import Tooltip from 'components/tooltip'
import SelectMenu from 'components/select/components/menu'
import { IContextMenuState, OptionProps } from 'types'
import { INITIAL_CONTEXT_MENU_STATE } from 'app-constants'

// The parts of a toolbar panel (Filter, Sort, Group, …), so each opens the same way and reads the
// same way. Everything sits on one 12px gutter under the floating menu's header (see FloatingMenu):
// the rules, a ghost "+ Add …" row, and a hairline section of settings.
//
// A rule is a pill: one bordered, 32px control made of segments, each of them a
// button that opens its own list (the column, the condition, the value) or a choice made in place
// (a direction, and / or), with a × as its last segment. A pill is only as wide as what it says.

/** The panel widths. A panel picks one rather than a width of its own. */
export const PANEL_WIDTH = { sm: 320, md: 360, lg: 480 } as const

/** Where a panel opens: 6px under its button, the two right edges lined up (Menu keeps it on screen). */
export const panelAnchor = (trigger: Element, width: number): Pick<IContextMenuState, 'top' | 'left'> => {
  const rect = trigger.getBoundingClientRect()
  return { top: `${rect.bottom + 6}px`, left: `${rect.right - width}px` }
}

// style.scss lifts every focused <button> with a hard offset shadow; panel buttons don't move.
const STILL = 'focus:shadow-none! focus:transform-none!'

/**
 * A ghost button: quiet text that gets a fill on hover. Its padding hangs out into the gutter
 * (negative margin), so its text lines up with everything else while its hover fill has room.
 */
const ghost = tv({
  base: `h-7 gap-1.5 rounded-md px-2 text-[13px] font-normal text-muted hover:bg-surface-hover hover:text-body [&_svg]:size-4 ${STILL}`,
  variants: {
    // The header's action: the menu rows' 13px medium, quieter.
    size: { sm: 'h-7 px-2 text-[13px] font-medium' },
    // Quiet until hovered, then red: an action that removes things ("Clear all").
    destructive: { true: 'hover:bg-red/10 hover:text-red' }
  }
})

/** A small quiet action: the header's "Clear all", Group's "Collapse all". */
export const PanelAction: React.FC<{
  onClick: () => void
  children: React.ReactNode
  disabled?: boolean
  icon?: React.ReactNode
  /** Red on hover: for an action that removes things. */
  destructive?: boolean
}> = ({ onClick, children, disabled, icon, destructive }) => (
  <Button
    internalType="transparent"
    className={ghost({ size: 'sm', destructive })}
    onClick={onClick}
    disabled={disabled}
  >
    {icon}
    {children}
  </Button>
)

// ——— Pills ———————————————————————————————————————————————————————————————————————————————

/** The pill: a bordered 32px control whose children are segments, set apart by their own padding. */
export const PanelPill: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    data-panel-pill
    className="inline-flex h-8 max-w-full min-w-0 items-stretch overflow-hidden rounded-lg border border-divider bg-field shadow-[0_1px_2px_rgb(0_0_0/0.04)]"
  >
    {children}
  </div>
)

// ——— Room for a segment ——————————————————————————————————————————————————————————————————

const SEGMENT_PADDING = 12 // px-1.5 on each side
const FALLBACK_FONT = '600 13px Inter'
let measuringCanvas: HTMLCanvasElement | null = null

/** How wide `text` is drawn in `font`. */
const textWidth = (text: string, font: string): number => {
  measuringCanvas ??= document.createElement('canvas')
  const context = measuringCanvas.getContext('2d')
  if (!context) return text.length * 7
  context.font = font
  return context.measureText(text).width
}

/**
 * The widest a segment can grow: what its rules list has across, less what the pill's other
 * segments take. A pill can reach the panel's right edge, never past it. Null until measured.
 */
const useSegmentRoom = (ref: React.RefObject<HTMLElement | null>): { room: number | null; font: string } => {
  const [room, setRoom] = useState<number | null>(null)
  const [font, setFont] = useState(FALLBACK_FONT)
  useLayoutEffect(() => {
    const segment = ref.current
    const pill = segment?.closest<HTMLElement>('[data-panel-pill]')
    const list = segment?.closest<HTMLElement>('[data-panel-rules]')
    if (!segment || !pill || !list) return
    const measure = () => {
      const style = getComputedStyle(list)
      const across = list.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight)
      // The other segments' own widths, and the pill's border: not the pill's width less this one's,
      // which comes out short once this segment overflows the pill (capped at the panel's width).
      const pillStyle = getComputedStyle(pill)
      const border = parseFloat(pillStyle.borderLeftWidth) + parseFloat(pillStyle.borderRightWidth)
      const others = [...pill.children]
        .filter((child) => !child.contains(segment))
        .reduce((sum, child) => sum + child.getBoundingClientRect().width, border)
      setRoom(Math.max(0, Math.floor(across - others)))
      setFont(getComputedStyle(segment).font)
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(list)
    return () => observer.disconnect()
  })
  return { room, font }
}

/**
 * As many of the picked labels as fit in `room`, and how many are left for a count: "A, B, C" +4.
 * When even the first doesn't fit, it's shown alone, to be cut short with an ellipsis before the count.
 */
const fittedLabel = (labels: string[], room: number, font: string): { text: string; more: number } => {
  for (let shown = labels.length; shown > 0; shown--) {
    const more = labels.length - shown
    const text = labels.slice(0, shown).join(', ')
    if (textWidth(text + (more ? ` +${more}` : ''), font) <= room) return { text, more }
  }
  return { text: labels[0] ?? '', more: Math.max(0, labels.length - 1) }
}

const segment = tv({
  base: `h-full min-w-0 shrink-0 gap-1.5 rounded-none border-0 px-1.5 text-[13px] font-normal whitespace-nowrap hover:bg-surface-hover focus-visible:bg-surface-hover focus-visible:outline-none [&_svg]:size-4 [&_svg]:shrink-0 ${STILL}`,
  variants: {
    // What the segment holds: a column (set as the grid's column headers set it: semibold, a 14px
    // icon), a value (semibold too: what was picked reads as strongly as what it was picked for), the
    // condition (muted), nothing yet, or the × (quiet until hovered, then red).
    tone: {
      field: 'gap-1 text-body font-semibold hover:text-body [&_svg]:size-3.5',
      strong: 'text-body font-semibold hover:text-body',
      muted: 'text-muted hover:text-body',
      placeholder: 'text-faint hover:text-muted',
      danger: 'text-muted hover:bg-red/10 hover:text-red focus-visible:bg-red/10 focus-visible:text-red'
    },
    icon: { true: 'w-8 justify-center px-0' }
  },
  defaultVariants: { tone: 'strong' }
})

type SegmentTone = 'field' | 'strong' | 'muted' | 'placeholder' | 'danger'

interface PanelSegmentButtonProps {
  onClick: () => void
  /** Named in a tooltip, and to a screen reader when the segment is only an icon. */
  label: string
  children: React.ReactNode
  tone?: SegmentTone
  icon?: boolean
  disabled?: boolean
}

/** A segment that acts in place: flips "and" to "or", removes the rule. */
export const PanelSegmentButton: React.FC<PanelSegmentButtonProps> = ({
  onClick,
  label,
  children,
  tone,
  icon,
  disabled
}) => (
  <Tooltip content={label}>
    <Button
      internalType="transparent"
      className={segment({ tone, icon })}
      aria-label={icon ? label : undefined}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </Button>
  </Tooltip>
)

interface PanelSegmentChoiceProps<T extends string> {
  options: Array<{ value: T; label: string; icon: React.ReactNode }>
  value: T
  onChange: (value: T) => void
  /** What's being chosen, for a screen reader: "Direction for Name". */
  ariaLabel: string
  /** The words that lead into the choice, as a muted segment of their own: "sorted by". */
  lead?: string
  disabled?: boolean
}

/**
 * One of a few choices, each with an icon: a sort's direction. The segment shows the chosen one's
 * icon (named in a tooltip) and opens a menu of them all.
 */
export const PanelSegmentChoice = <T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  lead,
  disabled
}: PanelSegmentChoiceProps<T>): React.ReactElement => {
  const [list, setList] = useState<IContextMenuState>(INITIAL_CONTEXT_MENU_STATE)
  const chosen = options.find((option) => option.value === value) ?? options[0]
  const close = () => setList(INITIAL_CONTEXT_MENU_STATE)

  return (
    <>
      {lead && (
        <span
          aria-hidden
          className="flex shrink-0 items-center px-1.5 text-[13px] whitespace-nowrap text-muted select-none"
        >
          {lead}
        </span>
      )}
      <Tooltip content={chosen.label}>
        <Button
          internalType="transparent"
          className={segment({ icon: true })}
          aria-label={`${ariaLabel}: ${chosen.label}`}
          aria-haspopup="menu"
          aria-expanded={list.open}
          disabled={disabled}
          onClick={(event) => {
            const rect = event.currentTarget.getBoundingClientRect()
            setList({ open: true, top: `${rect.bottom + 4}px`, left: `${rect.left}px`, right: 'auto', bottom: 'auto' })
          }}
        >
          {chosen.icon}
        </Button>
      </Tooltip>
      {list.open && (
        // Nested, so choosing here doesn't count as a click outside the panel.
        <Menu id="panel-choice-menu" nested menuState={list} setMenuState={setList} width={240} zIndex={3100}>
          <MenuList>
            <div role="menu" aria-label={ariaLabel} className="flex flex-col">
              {options.map((option) => (
                <MenuItem
                  key={option.value}
                  role="menuitemradio"
                  aria-checked={option.value === value}
                  icon={option.icon}
                  end={option.value === value ? <Tick className="size-4 text-body" /> : undefined}
                  onClick={() => {
                    if (option.value !== value) onChange(option.value)
                    close()
                  }}
                >
                  {option.label}
                </MenuItem>
              ))}
            </div>
          </MenuList>
        </Menu>
      )}
    </>
  )
}

interface PanelSegmentSelectProps {
  options: OptionProps[]
  selected: string[]
  onPick: (value: string) => void
  /** What the segment picks, for a screen reader: "Column", "Condition". */
  ariaLabel: string
  /** Shown when nothing is picked yet. */
  placeholder?: string
  tone?: SegmentTone
  multiselect?: boolean
  searchable?: boolean
  loading?: boolean
  /** The list's width; at least the segment's own. */
  menuWidth?: number
  /** Caps the segment's width (e.g. max-w-[200px]); its text is then cut short with an ellipsis. */
  className?: string
  /** Open the list as soon as the segment appears (a filter added from a column's own menu). */
  defaultOpen?: boolean
  disabled?: boolean
  /** Words that lead into the pick, as a muted segment of their own: "locks". */
  lead?: string
}

const labelsOf = (options: OptionProps[], selected: string[]): string[] =>
  selected.map((value) => options.find((option) => option.value === value)?.label ?? value)

/**
 * A list that opens 4px under its trigger, at least as wide as the trigger. `alignEnd` lines up the
 * two right edges instead of the left ones (a setting row, whose value sits at its right).
 */
const useListUnder = (menuWidth?: number, alignEnd?: boolean) => {
  const [list, setList] = useState<IContextMenuState>(INITIAL_CONTEXT_MENU_STATE)
  const [width, setWidth] = useState(0)
  const open = (trigger: HTMLElement) => {
    const rect = trigger.getBoundingClientRect()
    const listWidth = alignEnd ? Math.max(menuWidth ?? 0, 160) : Math.max(rect.width, menuWidth ?? 0, 160)
    setWidth(listWidth)
    const left = alignEnd ? rect.right - listWidth : rect.left
    setList({ open: true, top: `${rect.bottom + 4}px`, left: `${left}px`, right: 'auto', bottom: 'auto' })
  }
  return { list, setList, width, open }
}

/** A segment that opens a list to pick from: the column, the condition, the values. */
export const PanelSegmentSelect: React.FC<PanelSegmentSelectProps> = ({
  options,
  selected,
  onPick,
  ariaLabel,
  placeholder = 'Select',
  tone = 'strong',
  multiselect,
  searchable = true,
  loading,
  menuWidth,
  className,
  defaultOpen,
  disabled,
  lead
}) => {
  const ref = useRef<HTMLButtonElement>(null)
  const { list, setList, width, open } = useListUnder(menuWidth)

  useEffect(() => {
    if (defaultOpen && ref.current) open(ref.current)
  }, [])

  // In a single pick '' is nothing picked; among several it's a value of its own (an empty cell).
  const picked = multiselect ? selected : selected.filter((value) => value !== '')
  // Several picked: as many as fit before the panel's edge, then "+N".
  const { room, font } = useSegmentRoom(ref)
  const labels = labelsOf(options, picked)
  const { text: label, more } =
    multiselect && room !== null
      ? fittedLabel(labels, room - SEGMENT_PADDING - 1, font)
      : { text: labels.join(', '), more: 0 }
  // One pick shows its icon (a column's type); several don't.
  const icon = picked.length === 1 && options.find((option) => option.value === picked[0])?.icon

  return (
    <>
      {lead && (
        <span
          aria-hidden
          className="flex shrink-0 items-center px-1.5 text-[13px] whitespace-nowrap text-muted select-none"
        >
          {lead}
        </span>
      )}
      <Button
        internalType="transparent"
        innerRef={ref}
        // It may give way (and end in an ellipsis) so the pill, and its ×, stay inside the panel.
        className={segment({ tone: picked.length ? tone : 'placeholder', class: ['shrink', className] })}
        // The pick in full for a screen reader, however much of it shows: "Column: In-House Design…".
        aria-label={picked.length ? `${ariaLabel}: ${labels.join(', ')}` : ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={list.open}
        disabled={disabled}
        onClick={(event) => open(event.currentTarget)}
      >
        {icon}
        {/* Cut short, or with values left to "+N": the whole of it on hover. */}
        <Tooltip content={labels.join(', ')} showOnlyIfTruncated={more === 0}>
          <span className="min-w-0 truncate">{picked.length ? label : placeholder}</span>
        </Tooltip>
        {more > 0 && <span className="shrink-0">+{more}</span>}
      </Button>
      {list.open && (
        // The id is one the Menu's click-outside already treats as part of the panel.
        <SelectMenu
          id="select-options"
          menuState={list}
          setMenuState={setList}
          options={options}
          optionsSelected={picked}
          onOptionClick={onPick}
          width={width}
          multiselect={multiselect}
          searchable={searchable}
          loading={loading}
        />
      )}
    </>
  )
}

/**
 * A segment you type a value into. At rest it's as wide as what's in it; while you type it takes the
 * room to the panel's edge, and gives it back when you leave.
 */
export const PanelSegmentInput = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  ({ className, value, placeholder, style, onFocus, onBlur, ...props }, ref) => {
    const inputRef = useRef<HTMLInputElement>(null)
    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement)
    const [typing, setTyping] = useState(false)
    const { room, font } = useSegmentRoom(inputRef)
    const hug = Math.ceil(textWidth(String(value ?? '') || placeholder || '', font)) + SEGMENT_PADDING + 2
    // At rest it hugs its text, but never past the panel's edge; typing, it takes all the room there
    // is (a longer value scrolls inside it).
    const width = room === null ? hug : typing ? room : Math.min(hug, room)

    return (
      <Input
        ref={inputRef}
        value={value}
        placeholder={placeholder}
        className={`h-full w-auto min-w-0 rounded-none border-0 bg-transparent px-1.5 font-semibold text-ellipsis transition-[width] duration-150 hover:border-0 focus:ring-0 placeholder:font-normal [&::-webkit-datetime-edit]:p-0 [&::-webkit-datetime-edit-fields-wrapper]:p-0 [&::-webkit-calendar-picker-indicator]:ml-1.5 [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-40 hover:[&::-webkit-calendar-picker-indicator]:opacity-80 ${className ?? ''}`}
        style={{ width, ...style }}
        onFocus={(event) => {
          setTyping(true)
          onFocus?.(event)
        }}
        onBlur={(event) => {
          setTyping(false)
          onBlur?.(event)
        }}
        {...props}
      />
    )
  }
)
PanelSegmentInput.displayName = 'PanelSegmentInput'

// ——— Rules ———————————————————————————————————————————————————————————————————————————————

const RulesContext = createContext(false)

interface PanelRulesProps {
  /** One id per rule, in order: what the drag-and-drop tracks. */
  ids: string[]
  /** Given, the rules can be dragged into a new order. */
  onReorder?: (from: number, to: number) => void
  children: React.ReactNode
}

/** The list of rules. With `onReorder`, each rule gets a handle to drag it by (or move it from the keyboard). */
export const PanelRules: React.FC<PanelRulesProps> = ({ ids, onReorder, children }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )
  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!onReorder || !over || active.id === over.id) return
    onReorder(ids.indexOf(String(active.id)), ids.indexOf(String(over.id)))
  }
  const sortable = !!onReorder && ids.length > 1

  return (
    <RulesContext.Provider value={sortable}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis]}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          <div data-panel-rules className="flex flex-col items-start gap-1 px-3 pt-1.5">
            {children}
          </div>
        </SortableContext>
      </DndContext>
    </RulesContext.Provider>
  )
}

interface PanelRuleProps {
  id: string
  /** What the × says: "Remove sort by Name". */
  removeLabel: string
  onRemove: () => void
  disabled?: boolean
  /** The pill's segments, before its ×. */
  children: React.ReactNode
  'data-testid'?: string
}

/** One rule: a pill of segments ending in a ×, with a drag handle in the gutter on hover when the list can be reordered. */
export const PanelRule: React.FC<PanelRuleProps> = ({ id, removeLabel, onRemove, disabled, children, ...data }) => {
  const sortable = useContext(RulesContext)
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: !sortable || disabled
  })

  return (
    <div
      {...data}
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={`group/rule relative flex max-w-full ${isDragging ? 'z-10' : ''}`}
    >
      {sortable && (
        <Button
          internalType="transparent"
          innerRef={setActivatorNodeRef}
          className={`absolute -left-3 top-1/2 h-6 w-3 -translate-y-1/2 rounded-sm p-0 text-faint opacity-0 cursor-grab touch-none hover:bg-transparent hover:text-body focus-visible:opacity-100 group-hover/rule:opacity-100 active:cursor-grabbing [&_svg]:size-3.5 ${STILL} ${isDragging ? 'opacity-100' : ''}`}
          aria-label="Drag to reorder"
          {...attributes}
          {...listeners}
        >
          <Drag />
        </Button>
      )}
      <PanelPill>
        {children}
        <PanelSegmentButton icon tone="danger" label={removeLabel} onClick={onRemove} disabled={disabled}>
          <Cross />
        </PanelSegmentButton>
      </PanelPill>
    </div>
  )
}

interface PanelAddProps {
  /** "Add sort", "Add filter", … */
  label: string
  /** The columns (or other things) a new rule can be made from. */
  options: OptionProps[]
  onPick: (value: string) => void
  disabled?: boolean
  noOptionsMessage?: React.ReactNode
  'data-tour'?: string
}

/** The ghost "+ Add …" row under the rules, opening a searchable list to pick the new rule's column from. */
export const PanelAdd: React.FC<PanelAddProps> = ({ label, options, onPick, disabled, noOptionsMessage, ...data }) => {
  const [picker, setPicker] = useState<IContextMenuState>(INITIAL_CONTEXT_MENU_STATE)

  return (
    <div className="flex items-center gap-1 px-3 pt-1.5 pb-3" {...data}>
      <Button
        internalType="outline"
        className={`h-8 gap-1.5 px-2.5 text-[13px] font-medium [&_svg]:size-4 [&_svg]:text-muted ${STILL}`}
        aria-haspopup="listbox"
        aria-expanded={picker.open}
        disabled={disabled}
        onClick={(event) => {
          const rect = event.currentTarget.getBoundingClientRect()
          setPicker({ open: true, top: `${rect.bottom + 4}px`, left: `${rect.left}px`, right: 'auto', bottom: 'auto' })
        }}
      >
        <Plus />
        {label}
      </Button>
      {picker.open && (
        // The id is one the Menu's click-outside already treats as part of the panel.
        <SelectMenu
          id="select-options"
          menuState={picker}
          setMenuState={setPicker}
          options={options}
          optionsSelected={[]}
          onOptionClick={onPick}
          width={240}
          searchable
          noOptionsMessage={noOptionsMessage}
        />
      )}
    </div>
  )
}

// ——— Settings ————————————————————————————————————————————————————————————————————————————

/**
 * A hairline, then a group of settings, set exactly as a menu sets its rows (the menu's own list,
 * label and rows): "View preferences", "Metadata filters".
 */
export const PanelSection: React.FC<{ label?: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="border-t border-divider">
    <MenuList>
      {label && <MenuLabel>{label}</MenuLabel>}
      {children}
    </MenuList>
  </div>
)

/** The panel's foot, on the menus' light grey band (MenuFooter): quiet actions on what the panel set up. */
export const PanelFooter: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex items-center gap-1 border-t border-divider bg-surface-muted px-1.5 py-1.5">{children}</div>
)

interface PanelSelectRowProps {
  label: string
  icon: React.ReactNode
  options: OptionProps[]
  selected: string
  onPick: (value: string) => void
  menuWidth?: number
}

/** A setting that's one of a few choices: a menu row with its icon and label, and the choice and a ⌄ at its end. */
export const PanelSelectRow: React.FC<PanelSelectRowProps> = ({
  label,
  icon,
  options,
  selected,
  onPick,
  menuWidth
}) => {
  const { list, setList, width, open } = useListUnder(menuWidth, true)
  const current = options.find((option) => option.value === selected)?.label
  // The row's end keeps its clicks to itself, so the choice opens the list from the row as well.
  const openFromRow = (event: React.SyntheticEvent<HTMLElement>) => {
    const row = event.currentTarget.closest<HTMLElement>('[role="button"]')
    if (row) open(row)
  }

  return (
    <>
      <MenuItem
        icon={icon}
        onClick={openFromRow}
        end={
          <span className="flex cursor-pointer items-center gap-1 font-normal text-muted" onClick={openFromRow}>
            {current}
            <ChevronDown className="size-3.5" />
          </span>
        }
      >
        {label}
      </MenuItem>
      {list.open && (
        <SelectMenu
          id="select-options"
          menuState={list}
          setMenuState={setList}
          options={options}
          optionsSelected={[selected]}
          onOptionClick={onPick}
          width={width}
          searchable={false}
        />
      )}
    </>
  )
}

/** A setting that's on or off: a menu row with its icon and label, and a switch at its end. */
export const PanelSwitch: React.FC<{
  label: string
  icon: React.ReactNode
  checked: boolean
  onChange: (checked: boolean) => void
}> = ({ label, icon, checked, onChange }) => (
  <MenuItem
    icon={icon}
    onClick={() => onChange(!checked)}
    end={<Toggle aria-label={label} toggled={checked} onToggle={onChange} />}
  >
    {label}
  </MenuItem>
)
